import { useMemo, useState, useEffect, useContext, useRef } from "react";
import Sidebar from "../components/Sidebar";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import { MemberContext } from "../context/MemberContext";
import { useNavigate } from "react-router-dom";
import { compressImage } from "../utils/imageCompression";
import { handleExportCSV, handleExportCSVWithFetch } from "../utils/csvExport";
import { toast } from "react-toastify";
import {
  AddMemberModal,
  DeleteUserModal,
  DisableUserModal,
  EnableUserModal,
  MemberStatusModal,
} from "../components/modals/memberModal";
import { useDateAndAdmin } from "../utils/dateUtils";
import { getAuthToken, getAdminProfileData } from "../utils/cookieUtils";
import { useLocations } from "../hooks/useLocations";

function MemberManagement() {
  const { formattedDate, adminData } = useDateAndAdmin();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const token = useMemo(() => getAuthToken(), []);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const [optionsPopupOpen, setOptionsPopupOpen] = useState(null);
  const [addMember, setAddMember] = useState(false);
  const [step, setStep] = useState(1);
  const [profileImage, setProfileImage] = useState(
    `${process.env.PUBLIC_URL}/images/upload-img.png`
  );
  const [imageId, setImageId] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const popupRef = useRef(null);
  const adminProfile = getAdminProfileData()?.message?.admin;
  const [memberData, setMemberData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    address: "",
    dateOfBirth: "",
    profession: "",
    NIN: "",
    location_id: null, 
    startWeight: "",
    startHeight: "",
    bodySize: "",
    profileImage: `${process.env.PUBLIC_URL}/images/upload-img.png`,
  });
  const [errors, setErrors] = useState({
    phoneNumber: "",
    NIN: "",
  });
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "phoneNumber" || name === "NIN") {
      if (/^\d{0,11}$/.test(value)) {
        setMemberData({ ...memberData, [name]: value });

        if (name === "phoneNumber" && value.length !== 11) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            phoneNumber: "Phone number must be exactly 11 digits",
          }));
        } else if (name === "phoneNumber") {
          setErrors((prevErrors) => ({
            ...prevErrors,
            phoneNumber: "",
          }));
        }

        if (name === "NIN" && value.length !== 11) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            NIN: "NIN must be 11 exactly digits",
          }));
        } else if (name === "NIN") {
          setErrors((prevErrors) => ({
            ...prevErrors,
            NIN: "",
          }));
        }
      }
    } else if (name === "location" || name === "location_id") {
      // Sync both location and location_id for AddMemberModal compatibility
      setMemberData({
        ...memberData,
        location: value,
        location_id: value ? Number(value) : null,
      });
    } else {
      setMemberData({ ...memberData, [name]: value });
    }
  };

  const handleDateChange = (date, name) =>
    setMemberData({ ...memberData, [name]: date });
  const handleNext = () => {
    let isValid = true;

    if (step === 1) {
      
      isValid =
        memberData.firstName &&
        memberData.lastName &&
        memberData.phoneNumber &&
        memberData.email &&
        memberData.address &&
        memberData.profession;

      if (!isValid) {
        toast.error("Please fill out all fields in Step 1.");
        return;
      }
    } else if (step === 2) {
      
      if (!imageId) {
        toast.error("Profile image is required.");
        return; 
      }

      
      isValid = memberData.startWeight && memberData.startHeight; 

      
      if (!isValid) {
        toast.error("Please fill out all fields in Step 2.");
        return;
      }
    }

    
    if (isValid) {
      setStep(step + 1);
    }
  };

  const handlePrevious = () => setStep((prevStep) => prevStep - 1);
  const handleOpenModal = () => setAddMember(true);
  const handleCloseModal = () => {
    setAddMember(false);
    setStep(1);
    setImageId(null);
    setProfileImage(`${process.env.PUBLIC_URL}/images/upload-img.png`);
    setMemberData({
      firstName: "",
      lastName: "",
      phoneNumber: "",
      email: "",
      address: "",
      dateOfBirth: "",
      profession: "",
      NIN: "",
      location: "",
      location_id: null,
      startWeight: "",
      startHeight: "",
      bodySize: "",
      profileImage: `${process.env.PUBLIC_URL}/images/upload-img.png`,
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileType = file.type;
      const validFileTypes = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "image/gif",
        "image/svg+xml",
      ];

      if (!validFileTypes.includes(fileType)) {
        toast.error(
          "Invalid file type. Please upload an image of type: jpeg, png, jpg, gif, svg."
        );
        return;
      }

      setImageUploading(true);

      try {
        
        const compressedFile = await compressImage(file);

        
        const previewURL = URL.createObjectURL(compressedFile);
        setProfileImage(previewURL);

        
        const formData = new FormData();
        formData.append("image", compressedFile);

        const response = await axiosInstance.post(
          "/store-customer-image",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.data.type === "success") {
          setImageId(response.data.message.image_id);
          setMemberData({ ...memberData, profileImage: previewURL });
          toast.success("Image uploaded successfully!");
        } else {
          toast.error("Failed to upload image. Please try again.");
          setProfileImage(`${process.env.PUBLIC_URL}/images/upload-img.png`);
        }
      } catch (error) {
        console.error("Error uploading image:", error);
        toast.error("Failed to upload image. Please try again.");
        setProfileImage(`${process.env.PUBLIC_URL}/images/upload-img.png`);
      } finally {
        setImageUploading(false);
      }
    }
  };

  const axiosInstance = useMemo(
    () =>
      axios.create({
        baseURL: "/super-admin",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }),
    [token]
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (locationDropdownOpen && !event.target.closest('.location-filter')) {
        setLocationDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [locationDropdownOpen]);

  const fetchMembers = async (page, locationId = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString() });
      if (locationId) {
        params.append('location_id', locationId);
      }
      const response = await axiosInstance.get(`/get-customers?${params.toString()}`);
      setMembers(response.data.message.data);
      setTotalPages(response.data.message.last_page);
    } catch (error) {
      console.error(
        "Error fetching members:",
        error.response ? error.response.data : error.message
      );
      toast.error("Failed to load members. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers(currentPage, selectedLocationId);
  }, [currentPage, selectedLocationId, axiosInstance]);

  const fetchSearchResults = async (searchTerm, locationId = "") => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ value: searchTerm });
      if (locationId) {
        params.append('location_id', locationId);
      }
      const response = await axiosInstance.get(
        `/search-customers?${params.toString()}`
      );
      setMembers(response.data.message.users.data);
      setTotalPages(response.data.message.users.last_page);
    } catch (error) {
      console.error(
        "Error searching members:",
        error.response ? error.response.data : error.message
      );
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  
  useEffect(() => {
    if (searchTerm === "") {
      fetchMembers(currentPage, selectedLocationId); 
    } else {
      fetchSearchResults(searchTerm, selectedLocationId); 
    }
  }, [currentPage, searchTerm, selectedLocationId]);

  const mapAccessStatus = (status) =>
    status === 1 ? "Enabled" : status === 0 ? "Disabled" : "Unknown";

  const handleLocationFilter = (locationId) => {
    setSelectedLocationId(locationId);
    setCurrentPage(1);
    setLocationDropdownOpen(false);
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      fetchMembers(pageNumber, selectedLocationId);
    }
  };

  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value); 
    setCurrentPage(1); 
  };

  const generatePageNumbers = () => {
    const pageNumbers = [];
    const range = 1;

    if (totalPages === 1) {
      return [1];
    }

    // Always show first page
    pageNumbers.push(1);

    if (totalPages <= 5) {
      // If 5 or fewer pages, show all
      for (let i = 2; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // For more than 5 pages, use ellipsis logic
      if (currentPage <= 3) {
        // Near the beginning
        for (let i = 2; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push("...");
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Near the end
        pageNumbers.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          if (i > 1) pageNumbers.push(i);
        }
      } else {
        // In the middle
        pageNumbers.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push("...");
        pageNumbers.push(totalPages);
      }
    }

    // Remove duplicates while preserving order
    return pageNumbers.filter((page, index, self) => 
      index === self.findIndex(p => p === page)
    );
  };

  const openOptionsPopup = (index) =>
    setOptionsPopupOpen(optionsPopupOpen === index ? null : index);

  const getSubscriptionStatusColor = (status) =>
    status === "subscribed"
      ? "green"
      : status === "expired"
      ? "red"
      : status === "inactive"
      ? "orange"
      : "#737373";

  const handleComplete = async () => {
    
    if (!imageId) {
      toast.error("Profile image is required.");
      return; 
    }

    
    const formattedDob = formatDate(memberData.dateOfBirth);

    
    const requestData = {
      f_name: memberData.firstName,
      l_name: memberData.lastName,
      phone: memberData.phoneNumber,
      email: memberData.email,
      address: memberData.address,
      profession: memberData.profession,
      weight: memberData.startWeight,
      height: memberData.startHeight,
      image_id: imageId,
      location_id: memberData.location_id,
    };

    
    if (memberData.dateOfBirth) {
      requestData.dob = formattedDob;
    }

    if (memberData.NIN) {
      requestData.nin = memberData.NIN;
    }

    if (memberData.bodySize) {
      requestData.body_size = memberData.bodySize;
    }

    try {
      const response = await axiosInstance.post(
        "/create-customer-account",
        requestData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      handleCloseModal(); 
      setCompleteSuccess(true); 
    } catch (error) {
      let errorMessage = "Something went wrong while submitting the form.";

      if (error.response?.data?.errors) {
        const fieldErrors = Object.keys(error.response.data.errors)
          .map((field) => `${field}: ${error.response.data.errors[field]}`)
          .join("\n");

        errorMessage = `Error creating member:\n${fieldErrors}`;
        
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
      console.error("Error submitting form:", errorMessage);
    }
  };

  
  const formatDate = (date) => {
    const d = new Date(date);
    let day = d.getDate();
    let month = d.getMonth() + 1; 
    const year = d.getFullYear();

    if (day < 10) {
      day = "0" + day;
    }
    if (month < 10) {
      month = "0" + month;
    }

    return `${day}-${month}-${year}`;
  };

  const [selectedMember, setSelectedMember] = useState(null);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDisableModalOpen, setDisableModalOpen] = useState(false);
  const [isEnableModalOpen, setEnableModalOpen] = useState(false);
  const [completeSuccess, setCompleteSuccess] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [disableSuccess, setDisableSuccess] = useState(false);
  const [enableSuccess, setEnableSuccess] = useState(false);
  const { locations } = useLocations(true);
  const [selectedLocation, setSelectedLocation] = useState("");

  // Fetch members by location
  const handleLocationSelect = async (e) => {
    const locId = e.target.value;
    setSelectedLocation(locId);
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/get-customers?location_id=${locId}`);
      setMembers(response.data.message.data);
      setTotalPages(response.data.message.last_page);
    } catch (error) {
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteModal = (member) => {
    setSelectedMember(member);
    setDeleteModalOpen(true);
    setOptionsPopupOpen(false);
  };

  const handleOpenDisableModal = (member) => {
    setSelectedMember(member);
    setDisableModalOpen(true);
    setOptionsPopupOpen(false);
  };

  const handleOpenEnableModal = (member) => {
    setSelectedMember(member);
    setEnableModalOpen(true);
    setOptionsPopupOpen(false);
  };

  const handleCloseModals = () => {
    setDeleteModalOpen(false);
    setDisableModalOpen(false);
    setEnableModalOpen(false);
    setSelectedMember(null);
    setOptionsPopupOpen(false);
  };

  const handleDeleteUser = async () => {
    try {
      const response = await axiosInstance.post("/delete-user", {
        user_id: selectedMember.id,
      });
      setMembers((prevMembers) =>
        prevMembers.filter((member) => member.id !== selectedMember.id)
      );
      handleCloseModals();
      setDeleteSuccess(true);
    } catch (error) {
      toast.error(error.response.data.message);
      console.error(
        "Error deleting user:",
        error.response ? error.response.data : error.message
      );
    }
  };

  const handleDisableUser = async () => {
    try {
      const response = await axiosInstance.post("/disable-user-access", {
        user_id: selectedMember.id,
      });
      setMembers((prevMembers) =>
        prevMembers.map((member) =>
          member.id === selectedMember.id
            ? { ...member, access_status: 0 }
            : member
        )
      );
      handleCloseModals();
      setDisableSuccess(true);
    } catch (error) {
      toast.error(error.response.data.message);
      console.error(
        "Error disabling user:",
        error.response ? error.response.data : error.message
      );
    }
  };

  const handleEnableUser = async () => {
    try {
      const response = await axiosInstance.post("/enable-user-access", {
        user_id: selectedMember.id,
      });
      setMembers((prevMembers) =>
        prevMembers.map((member) =>
          member.id === selectedMember.id
            ? { ...member, access_status: 1 }
            : member
        )
      );
      handleCloseModals();
      setEnableSuccess(true);
    } catch (error) {
      console.error(
        "Error enabling user:",
        error.response ? error.response.data : error.message
      );
      toast.error(error.response.data.message);
    }
  };

  const navigate = useNavigate();
  const { setSelectedMemberId } = useContext(MemberContext);

  const handleViewProfile = (memberId) => {
    setSelectedMemberId(memberId); 
    navigate("/view-member");
  };

  const closeOptionsPopup = () => {
    setOptionsPopupOpen(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        closeOptionsPopup();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [popupRef]);

  return (
    <div className="dashboard container-fluid">
      <div className="Nav">
        <Sidebar />
      </div>
      <div className="dashboard-display container-fluid">
        <div className="top-bar container-fluid">
          <div className="top-bar-info">
            <p id="date">{formattedDate}</p>
            <b>
              <p>Welcome back {adminData}</p>
            </b>
          </div>
          <div className="top-bar-button">
            <button onClick={handleOpenModal}>
              <img
                src={`${process.env.PUBLIC_URL}/images/add-user.png`}
                alt="Add member"
                style={{ height: "15px" }}
              />{" "}
              Add Member
            </button>
          </div>
        </div>
        <div className="main-dashboard container-fluid sub-dashboard">
          <div
            style={{ padding: "24px", paddingBottom: "0", paddingTop: "0" }}
            className="main-settings staff-items"
          >
            <div className="search">
              <input
                type="text"
                placeholder="Search fields"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
             <div className="export-group">
              {/* Location Filter - Only for Super Admin */}
              {adminProfile?.role_id === 3 && (
                <div className="location-filter">
                  <button
                    className={`location-filter-button ${selectedLocationId ? 'active' : ''}`}
                    onClick={() => setLocationDropdownOpen(!locationDropdownOpen)}
                  >
                    <span>
                      {selectedLocationId 
                        ? locations.find(loc => loc.id.toString() === selectedLocationId)?.name || 'Select Location'
                        : 'All Locations'
                      }
                    </span>
                    <span className={`location-filter-arrow ${locationDropdownOpen ? 'open' : ''}`}>
                      <svg className="filter-arrow-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  </button>
                  {locationDropdownOpen && (
                    <div className="location-filter-dropdown">
                      <div
                        className={`location-filter-option ${!selectedLocationId ? 'selected' : ''}`}
                        onClick={() => handleLocationFilter("")}
                      >
                        All Locations
                      </div>
                      {locations.map(location => (
                        <div
                          key={location.id}
                          className={`location-filter-option ${selectedLocationId === location.id.toString() ? 'selected' : ''}`}
                          onClick={() => handleLocationFilter(location.id.toString())}
                        >
                          {location.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="export">
                <button onClick={() => handleExportCSVWithFetch(axiosInstance, selectedLocationId)}>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/export-icon.png`}
                    alt="Export"
                  />{" "}
                  Export all
                </button>
              </div>

              <div className="export">
                <button onClick={() => handleExportCSV(members)}>
                  <img
                    src={`${process.env.PUBLIC_URL}/images/export-icon.png`}
                    alt="Export"
                  />{" "}
                  Export Page
                </button>
              </div>
            </div>
          </div>
          <div style={{ marginTop: "0" }} className="show-admin-table">
            <table className="admin-table member-table">
              <thead>
                <tr>
                  <th>Gym Number</th>
                  <th>Name</th>
                  <th>Location</th>
                  <th>Total Gym Hours</th>
                  <th>Access Status</th>
                  <th>Subscription Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7">
                      <div className="spinner"></div>
                    </td>
                  </tr>
                ) : members.length > 0 ? (
                  members.map((member) => (
                    <tr key={member.id}>
                      <td>{member.id}</td>
                      <td>
                        {member.f_name} {member.l_name}
                      </td>
                      <td>{member.location ? member.location.name : ''}</td>
                      <td>{member.total_gym_hours || "N/A"}</td>
                      <td>{mapAccessStatus(member.access_status)}</td>
                      <td
                        style={{
                          color: getSubscriptionStatusColor(
                            member.subscription_status
                          ),
                          fontWeight: "700",
                        }}
                      >
                        {member.subscription_status || "N/A"}
                      </td>
                      <td>
                        <div className="options-menu">
                          <img
                            src={`${process.env.PUBLIC_URL}/images/horizontal-menu.png`}
                            onClick={() => openOptionsPopup(member.id)}
                            alt="Options menu"
                          />
                          {optionsPopupOpen === member.id && (
                            <div className="popup" ref={popupRef}>
                              <ul>
                                <li
                                  style={{ color: "#0473BD" }}
                                  onClick={() => handleViewProfile(member.id)}
                                >
                                  <img
                                    src={`${process.env.PUBLIC_URL}/images/eye.png`}
                                    alt="View"
                                  />
                                  View Profile
                                </li>
                                {member.access_status === 0 && (
                                  <li
                                    onClick={() =>
                                      handleOpenEnableModal(member)
                                    }
                                  >
                                    <img
                                      src={`${process.env.PUBLIC_URL}/images/enable-user.png`}
                                      alt="Enable"
                                    />
                                    Enable Access
                                  </li>
                                )}
                                {member.access_status === 1 && (
                                  <li
                                    onClick={() =>
                                      handleOpenDisableModal(member)
                                    }
                                    style={{ color: "#DD9828" }}
                                  >
                                    <img
                                      src={`${process.env.PUBLIC_URL}/images/disable-user.png`}
                                      alt="Disable"
                                    />
                                    Disable Access
                                  </li>
                                )}
                                <li
                                  onClick={() => handleOpenDeleteModal(member)}
                                  style={{ color: "red" }}
                                >
                                  <img
                                    src={`${process.env.PUBLIC_URL}/images/staff-delete.png`}
                                    alt="Delete"
                                  />
                                  Delete Profile
                                </li>
                              </ul>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" style={{ textAlign: "center" }}>
                      No members found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className="pagination staff-pagination">
              <div>
                {/* Previous button */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                  aria-label="Previous page"
                >
                  {loading ? (
                    <span className="loading-spinner" aria-hidden="true">
                      ⏳
                    </span> 
                  ) : (
                    "<" 
                  )}
                </button>

                {/* Display page numbers */}
                {generatePageNumbers().map((page, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      if (page !== "...") {
                        handlePageChange(page); 
                      }
                    }}
                    className={page === currentPage ? "active" : ""}
                    disabled={page === "..." || loading}
                    style={{ width: "auto", minWidth:'30px' }}
                  >
                    {page}
                  </button>
                ))}

                {/* Next button */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || loading}
                  aria-label="Next page"
                >
                  {loading ? (
                    <span className="loading-spinner" aria-hidden="true">
                      ⏳
                    </span> 
                  ) : (
                    ">" 
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AddMemberModal
        isOpen={addMember}
        onRequestClose={handleCloseModal}
        step={step}
        memberData={memberData}
        handleInputChange={handleInputChange}
        handleDateChange={handleDateChange}
        handleFileChange={handleFileChange}
        handleNext={handleNext}
        handlePrevious={handlePrevious}
        handleComplete={handleComplete}
        profileImage={profileImage}
        imageUploading={imageUploading}
        errors={errors}
      />

      <MemberStatusModal
        isOpen={completeSuccess}
        onRequestClose={() => setCompleteSuccess(false)}
        type="success"
        title="Successful"
        description="Member has been added successfully"
        buttonText="Cancel"
        onButtonClick={() => {
          setCompleteSuccess(false);
          window.location.reload();
        }}
      />

      <DeleteUserModal
        isOpen={isDeleteModalOpen}
        handleClose={handleCloseModals}
        userProfile={selectedMember}
        onConfirmDelete={handleDeleteUser}
      />

      <MemberStatusModal
        isOpen={deleteSuccess}
        onRequestClose={() => setDeleteSuccess(false)}
        type="success"
        title="User Profile Deleted"
        buttonText="Cancel"
      />

      <DisableUserModal
        isOpen={isDisableModalOpen}
        handleCloseModals={handleCloseModals}
        userProfile={selectedMember}
        handleDisableUser={handleDisableUser}
      />

      <MemberStatusModal
        isOpen={disableSuccess}
        onRequestClose={() => setDisableSuccess(false)}
        type="success"
        title="User Profile Disabled"
        buttonText="Cancel"
      />

      <EnableUserModal
        isOpen={isEnableModalOpen}
        onRequestClose={handleCloseModals}
        selectedMember={selectedMember}
        onEnableUser={handleEnableUser}
      />

      <MemberStatusModal
        isOpen={enableSuccess}
        onRequestClose={() => setEnableSuccess(false)}
        type="success"
        title="Access Granted"
        buttonText="Cancel"
      />
    </div>
  );
}

export default MemberManagement;
